/**
 * Brick Armory — product page behaviour (defer; sections/main-product and
 * sections/product-recommendations).
 *
 * <brick-product-info>      Section root. Owns the variant JSON, keeps the
 *                           hidden id input, buttons and URL in sync, jumps the
 *                           gallery, and re-renders every [data-swap] region
 *                           (price, stock, SKU, sticky price) through the
 *                           Section Rendering API — no reload.
 * <brick-variant-picker>    Radios -> variant; marks unavailable values.
 * <brick-product-gallery>   Scroll-snap viewer (swipe on mobile), thumbs,
 *                           arrows, 01 / 05 counter, opens <brick-lightbox>.
 * <brick-deferred-media>    Mounts video / 3D model on demand.
 * <brick-quantity>          Stepper.
 * <brick-sticky-atc>        Bar shown once the main button scrolls away.
 * <brick-accessories>       Main model + ticked accessories in ONE
 *                           /cart/add.js call, then opens the cart drawer.
 * <brick-share>             Native share sheet, clipboard fallback.
 * <brick-recommendations>   Lazy related products (Section Rendering API).
 *
 * The Add to cart form itself is a plain POST to /cart/add — the cart drawer
 * (assets/cart-drawer.js) intercepts it and fires `brick:cart:add`.
 */
(() => {
  if (window.BrickProductLoaded) return;
  window.BrickProductLoaded = true;

  const theme = (window.BrickTheme = window.BrickTheme || {});
  const root = (theme.routes && theme.routes.root) || '/';
  const motionOff = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => (motionOff() ? 'auto' : 'smooth');
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  const pad = (n) => String(n).padStart(2, '0');

  /* Money in the shop's format (same rules as predictive-search.js). */
  const formatMoney = (cents) => {
    const format = theme.moneyFormat || '{{amount}}';
    const value = Number(cents) / 100;
    const group = (number, precision, thousands, decimal) => {
      const [whole, fraction] = number.toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const text = format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return group(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
    // Formats may contain markup (e.g. <span>); we only ever set textContent.
    return text.replace(/<[^>]*>/g, '');
  };

  /* ---------------------------------------------------------------------- */
  /* <brick-product-info>                                                   */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-product-info',
    class BrickProductInfo extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-product-json]');
        try {
          this.variants = json ? JSON.parse(json.textContent).variants : [];
        } catch (error) {
          this.variants = [];
        }
        this.sectionId = this.dataset.sectionId;
        const input = this.querySelector('[data-variant-input]');
        this.current = this.variants.find((v) => String(v.id) === String(input && input.value)) || null;

        this.addEventListener('brick:variant-select', (event) => this.onVariant(event.detail.variant));
        this.onAdded = (event) => this.flashAdded(event.detail || {});
        document.addEventListener('brick:cart:add', this.onAdded);
      }

      disconnectedCallback() {
        document.removeEventListener('brick:cart:add', this.onAdded);
        if (this.controller) this.controller.abort();
      }

      quantity() {
        const input = this.querySelector('input[name="quantity"]');
        return Math.max(1, parseInt(input && input.value, 10) || 1);
      }

      onVariant(variant) {
        this.current = variant || null;
        const available = Boolean(variant && variant.available);

        this.querySelectorAll('[data-variant-input]').forEach((input) => {
          input.value = variant ? variant.id : '';
        });

        const label = available
          ? this.dataset.textAdd
          : variant
            ? this.dataset.textSoldOut
            : this.dataset.textUnavailable;
        this.querySelectorAll('[data-submit]').forEach((button) => {
          button.disabled = !available;
          delete button.dataset.state;
          const text = button.querySelector('[data-submit-label]');
          if (text) text.textContent = label;
        });

        if (variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url.toString());

          const gallery = this.querySelector('brick-product-gallery');
          if (gallery && variant.media) gallery.showMedia(variant.media);
          this.applyVariantData(variant);
          this.renderVariant(variant.id);
        } else {
          const stock = this.querySelector('[data-swap="stock"]');
          if (stock) {
            stock.innerHTML = '';
            const line = document.createElement('p');
            line.className = 'stock stock--unavailable';
            line.innerHTML = '<span class="stock__dot" aria-hidden="true"></span>';
            const span = document.createElement('span');
            span.textContent = this.dataset.textUnavailable;
            line.append(span);
            stock.append(line);
          }
        }

        this.dispatchEvent(new CustomEvent('brick:product:variant', { detail: { variant } }));
      }

      /* Instant update from the variant JSON (price, sticky price, SKU);
         the Section Rendering response below then refines every region
         (stock, unit price…) with server markup. */
      applyVariantData(variant) {
        const row = this.querySelector('[data-swap="price"] .product-price__row');
        if (row) {
          const price = Number(variant.price) || 0;
          const compare = Number(variant.compare) || 0;
          const onSale = compare > price;
          const make = (tag, className, text) => {
            const el = document.createElement(tag);
            if (className) el.className = className;
            el.textContent = text;
            return el;
          };
          const parts = [];
          if (onSale) {
            parts.push(make('span', 'visually-hidden', this.dataset.textSalePrice));
            parts.push(make('span', 'product-price__current price price--sale', formatMoney(price)));
            parts.push(make('span', 'visually-hidden', this.dataset.textRegularPrice));
            parts.push(make('s', 'product-price__compare price price--compare', formatMoney(compare)));
            const percent = Math.round(((compare - price) * 100) / compare);
            if (percent > 0 && row.querySelector('.product-price__save')) {
              parts.push(make('span', 'product-price__save mono', (this.dataset.textSave || '').replace('[percent]', percent)));
            }
          } else {
            parts.push(make('span', 'visually-hidden', this.dataset.textPrice));
            parts.push(make('span', 'product-price__current price', formatMoney(price)));
          }
          row.replaceChildren(...parts);
          row.closest('.product-price')?.classList.toggle('product-price--sale', onSale);
        }
        this.querySelectorAll('[data-swap="sticky-price"]').forEach((el) => (el.textContent = formatMoney(variant.price)));
        this.querySelectorAll('[data-swap="sticky-variant"]').forEach((el) => (el.textContent = variant.title));
        this.querySelectorAll('[data-swap="sku"]').forEach((el) => {
          el.textContent = variant.sku ? (this.dataset.textRef || '').replace('[sku]', variant.sku) : '';
        });
      }

      /* Fresh price / stock / SKU markup for the chosen variant. */
      async renderVariant(id) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const regions = Array.from(this.querySelectorAll('[data-swap]'));
        if (!regions.length) return;
        regions.forEach((el) => el.setAttribute('aria-busy', 'true'));

        try {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('variant', id);
          url.searchParams.set('section_id', this.sectionId);
          const response = await fetch(url.toString(), { signal: this.controller.signal });
          if (!response.ok) throw new Error(String(response.status));
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          regions.forEach((el) => {
            const fresh = doc.querySelector(`[data-swap="${el.dataset.swap}"]`);
            if (fresh) el.innerHTML = fresh.innerHTML;
          });
        } catch (error) {
          if (error.name === 'AbortError') return;
          /* keep the current markup; buttons/URL are already correct */
        } finally {
          regions.forEach((el) => el.removeAttribute('aria-busy'));
        }
      }

      flashAdded({ source }) {
        if (!(source instanceof Element) || !this.contains(source)) return;
        if (source.closest('brick-accessories')) return;
        clearTimeout(this.addedTimer);
        const buttons = Array.from(this.querySelectorAll('[data-submit]'));
        buttons.forEach((button) => {
          button.dataset.state = 'added';
          const text = button.querySelector('[data-submit-label]');
          if (text) text.textContent = this.dataset.textAdded;
        });
        const status = this.querySelector('[data-submit-status]');
        if (status) status.textContent = this.dataset.textAdded;
        this.addedTimer = setTimeout(() => {
          buttons.forEach((button) => {
            delete button.dataset.state;
            const text = button.querySelector('[data-submit-label]');
            if (text && !button.disabled) text.textContent = this.dataset.textAdd;
          });
          if (status) status.textContent = '';
        }, 2200);
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <brick-variant-picker>                                                 */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-variant-picker',
    class BrickVariantPicker extends HTMLElement {
      connectedCallback() {
        this.info = this.closest('brick-product-info');
        this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.addEventListener('change', () => this.onChange());
        this.markAvailability();
      }

      get variants() {
        return (this.info && this.info.variants) || [];
      }

      selected() {
        return this.fieldsets.map((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }

      onChange() {
        const values = this.selected();
        this.fieldsets.forEach((fieldset, index) => {
          const display = fieldset.querySelector('[data-option-value]');
          if (display) display.textContent = values[index] || '';
        });
        this.markAvailability();
        const variant = this.variants.find((v) => v.options.every((value, i) => value === values[i]));
        if (this.info) {
          this.info.dispatchEvent(new CustomEvent('brick:variant-select', { detail: { variant } }));
        }
      }

      /* A value is available if a purchasable variant has it together with
         the other currently selected options. */
      markAvailability() {
        const values = this.selected();
        this.fieldsets.forEach((fieldset, index) => {
          fieldset.querySelectorAll('input').forEach((input) => {
            const possible = this.variants.some(
              (v) =>
                v.available &&
                v.options[index] === input.value &&
                v.options.every((value, i) => i === index || value === values[i])
            );
            input.toggleAttribute('data-unavailable', !possible);
          });
        });
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <brick-product-gallery>                                                */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-product-gallery',
    class BrickProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
        this.rail = this.querySelector('[data-thumbs]');
        this.prev = this.querySelector('.product-gallery__controls [data-prev]');
        this.next = this.querySelector('.product-gallery__controls [data-next]');
        this.counter = this.querySelector('[data-counter-current]');
        this.active = 0;

        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumb))
        );
        if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.active - 1));
        if (this.next) this.next.addEventListener('click', () => this.goTo(this.active + 1));

        this.track.addEventListener('click', (event) => {
          const zoom = event.target.closest('[data-zoom]');
          if (!zoom) return;
          const lightbox = this.querySelector('brick-lightbox');
          if (lightbox && typeof lightbox.openAt === 'function') lightbox.openAt(zoom.dataset.zoom, zoom);
        });

        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.goTo(this.active + 1);
          if (event.key === 'ArrowLeft') this.goTo(this.active - 1);
        });

        if ('IntersectionObserver' in window && this.slides.length > 1) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
              });
            },
            { root: this.track, threshold: 0.6 }
          );
          this.slides.forEach((slide) => this.observer.observe(slide));
        }
        this.setActive(0);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      goTo(index) {
        const target = Math.max(0, Math.min(index, this.slides.length - 1));
        const slide = this.slides[target];
        if (!slide) return;
        this.track.scrollTo({ left: slide.offsetLeft, behavior: scrollBehavior() });
        this.setActive(target);
      }

      showMedia(mediaId) {
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index >= 0) this.goTo(index);
      }

      setActive(index) {
        if (index < 0 || !this.slides[index]) return;
        this.active = index;
        const id = this.slides[index].dataset.mediaId;

        this.slides.forEach((slide, i) => {
          if (i === index) return;
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('iframe').forEach((frame) => {
            try {
              frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
              frame.contentWindow.postMessage('{"method":"pause"}', '*');
            } catch (error) {
              /* cross-origin frames may refuse */
            }
          });
        });

        if (this.counter) this.counter.textContent = pad(index + 1);
        this.thumbs.forEach((thumb) => {
          const on = thumb.dataset.thumb === id;
          if (on) {
            thumb.setAttribute('aria-current', 'true');
            this.revealThumb(thumb);
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
        if (this.prev) this.prev.disabled = index === 0;
        if (this.next) this.next.disabled = index === this.slides.length - 1;
      }

      revealThumb(thumb) {
        const rail = this.rail;
        if (!rail) return;
        const item = thumb.parentElement;
        if (rail.scrollHeight > rail.clientHeight + 4) {
          rail.scrollTo({ top: item.offsetTop - rail.clientHeight / 2 + item.offsetHeight / 2, behavior: scrollBehavior() });
        } else if (rail.scrollWidth > rail.clientWidth + 4) {
          rail.scrollTo({ left: item.offsetLeft - rail.clientWidth / 2 + item.offsetWidth / 2, behavior: scrollBehavior() });
        }
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <brick-deferred-media>                                                 */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-deferred-media',
    class BrickDeferredMedia extends HTMLElement {
      connectedCallback() {
        const trigger = this.querySelector('[data-deferred-trigger]');
        if (trigger) trigger.addEventListener('click', () => this.load(true));

        // Short clips: mount + loop muted while in view (never with reduced motion).
        if (this.hasAttribute('data-clip') && !motionOff() && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.load(false);
                  this.resume();
                } else if (this.video) {
                  this.video.pause();
                }
              });
            },
            { threshold: 0.5 }
          );
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      resume() {
        if (this.video && !this.userPaused) this.video.play().catch(() => {});
      }

      mountClip(video, focus) {
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.removeAttribute('controls');

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'product-gallery__clip-toggle';
        const icons = {
          pause: '<svg class="icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor"><rect x="6.5" y="5.25" width="3.75" height="13.5" rx=".75"/><rect x="13.75" y="5.25" width="3.75" height="13.5" rx=".75"/></svg>',
          play: '<svg class="icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor"><path d="M8.5 5.9v12.2a.75.75 0 0 0 1.14.64l9.9-6.1a.75.75 0 0 0 0-1.28l-9.9-6.1a.75.75 0 0 0-1.14.64Z"/></svg>',
        };
        const sync = () => {
          const playing = !video.paused;
          toggle.innerHTML = playing ? icons.pause : icons.play;
          toggle.setAttribute('aria-label', (playing ? this.dataset.labelPause : this.dataset.labelPlay) || '');
        };
        toggle.addEventListener('click', () => {
          if (video.paused) {
            this.userPaused = false;
            video.play().catch(() => {});
          } else {
            this.userPaused = true;
            video.pause();
          }
        });
        video.addEventListener('play', sync);
        video.addEventListener('pause', sync);

        this.video = video;
        this.replaceChildren(video, toggle);
        sync();
        video.play().catch(() => {});
        if (focus) toggle.focus();
      }

      load(userInitiated = false) {
        if (this.loaded) return;
        const template = this.querySelector('template');
        if (!template) return;
        this.loaded = true;
        const content = template.content.firstElementChild.cloneNode(true);
        if (this.hasAttribute('data-clip') && content.tagName === 'VIDEO') {
          this.mountClip(content, userInitiated);
          return;
        }
        this.replaceChildren(content);
        if (content.tagName === 'VIDEO') {
          content.play().catch(() => {});
          content.focus();
        }
        if (this.dataset.type === 'model' && window.Shopify && window.Shopify.loadFeatures) {
          window.Shopify.loadFeatures([
            {
              name: 'model-viewer-ui',
              version: '1.0',
              onLoad: (errors) => {
                if (errors || !window.Shopify.ModelViewerUI) return;
                const viewer = this.querySelector('model-viewer');
                if (viewer) {
                  const ui = new window.Shopify.ModelViewerUI(viewer);
                  if (ui && ui.play) ui.play();
                }
              },
            },
          ]);
        }
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <brick-quantity>                                                       */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-quantity',
    class BrickQuantity extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        if (!this.input) return;
        this.buttons = Array.from(this.querySelectorAll('[data-step]'));
        this.buttons.forEach((button) =>
          button.addEventListener('click', () => this.set((Number(this.input.value) || 1) + Number(button.dataset.step)))
        );
        this.input.addEventListener('change', () => this.set(Number(this.input.value) || 1));
        this.sync();
      }

      set(value) {
        const min = Number(this.input.min) || 1;
        const max = Number(this.input.max) || 99;
        this.input.value = Math.max(min, Math.min(max, Math.round(value)));
        this.sync();
        this.dispatchEvent(new CustomEvent('brick:quantity-change', { bubbles: true, detail: { value: Number(this.input.value) } }));
      }

      sync() {
        const value = Number(this.input.value) || 1;
        const min = Number(this.input.min) || 1;
        const max = Number(this.input.max) || 99;
        this.buttons.forEach((button) => {
          const step = Number(button.dataset.step);
          button.disabled = step < 0 ? value <= min : value >= max;
        });
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <brick-sticky-atc>                                                     */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-sticky-atc',
    class BrickStickyAtc extends HTMLElement {
      connectedCallback() {
        const info = this.closest('brick-product-info');
        this.target = info && info.querySelector('[data-main-submit]');
        if (!this.target || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(([entry]) => {
          this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
        this.observer.observe(this.target);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.documentElement.classList.remove('has-sticky-atc');
      }

      toggle(show) {
        if (show === this.visible) return;
        this.visible = show;
        this.classList.toggle('is-visible', show);
        this.inert = !show;
        document.documentElement.classList.toggle('has-sticky-atc', show);
        if (show) document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <brick-accessories>                                                    */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-accessories',
    class BrickAccessories extends HTMLElement {
      connectedCallback() {
        this.info = this.closest('brick-product-info');
        this.button = this.querySelector('[data-accessory-add]');
        this.label = this.querySelector('[data-accessory-add-label]');
        this.totalEl = this.querySelector('[data-accessory-total]');
        this.mainPrice = this.querySelector('[data-accessory-main-price]');
        this.mainVariant = this.querySelector('[data-accessory-main-variant]');
        this.status = this.querySelector('[data-accessory-status]');
        if (!this.button) return;

        this.addEventListener('change', (event) => this.onChange(event));
        this.button.addEventListener('click', () => this.add());
        if (this.info) {
          this.refresh = () => this.update();
          this.info.addEventListener('brick:product:variant', this.refresh);
          this.info.addEventListener('brick:quantity-change', this.refresh);
        }
        this.update();
      }

      disconnectedCallback() {
        if (this.info && this.refresh) {
          this.info.removeEventListener('brick:product:variant', this.refresh);
          this.info.removeEventListener('brick:quantity-change', this.refresh);
        }
      }

      onChange(event) {
        const select = event.target.closest('[data-accessory-select]');
        if (select) {
          const item = select.closest('[data-accessory]');
          const chosen = select.selectedOptions[0];
          const check = item && item.querySelector('[data-accessory-check]');
          if (check && chosen) {
            check.value = chosen.value;
            check.dataset.price = chosen.dataset.price;
            const price = item.querySelector('[data-accessory-price]');
            if (price) price.textContent = formatMoney(chosen.dataset.price);
            if (!check.checked) check.checked = true;
          }
        }
        this.update();
      }

      checked() {
        return Array.from(this.querySelectorAll('[data-accessory-check]:checked'));
      }

      update() {
        const variant = this.info ? this.info.current : null;
        const quantity = this.info ? this.info.quantity() : 1;
        const main = variant ? Number(variant.price) * quantity : 0;
        const selected = this.checked();
        const extras = selected.reduce((sum, check) => sum + Number(check.dataset.price || 0), 0);

        if (this.mainPrice && variant) {
          this.mainPrice.textContent = (quantity > 1 ? `${quantity} × ` : '') + formatMoney(variant.price);
        }
        if (this.mainVariant && variant && this.mainVariant.textContent.trim()) {
          this.mainVariant.textContent = ` · ${variant.title}`;
        }
        if (this.totalEl) this.totalEl.textContent = formatMoney(main + extras);

        const ready = selected.length > 0 && Boolean(variant && variant.available);
        this.button.disabled = !ready;
        if (this.label) {
          this.label.textContent = selected.length
            ? (this.dataset.textAddCount || '').replace('[count]', String(selected.length + 1))
            : this.dataset.textAdd;
        }
        this.querySelectorAll('[data-accessory]').forEach((item) => {
          const check = item.querySelector('[data-accessory-check]');
          item.classList.toggle('is-selected', Boolean(check && check.checked));
        });
      }

      async add() {
        const variant = this.info ? this.info.current : null;
        const selected = this.checked();
        if (!variant || !selected.length || this.button.getAttribute('aria-busy') === 'true') return;

        clearTimeout(this.resetTimer);
        this.button.setAttribute('aria-busy', 'true');
        this.status.textContent = '';

        const drawer = document.querySelector('brick-cart-drawer');
        const items = [
          { id: Number(variant.id), quantity: this.info.quantity() },
          ...selected.map((check) => ({ id: Number(check.value), quantity: 1 })),
        ];
        const body = { items };
        if (drawer && drawer.dataset.sectionId) {
          body.sections = [drawer.dataset.sectionId];
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data.status) throw new Error(data.description || data.message || '');

          this.button.removeAttribute('aria-busy');
          this.button.dataset.state = 'added';
          this.status.textContent = this.dataset.textAdded;
          if (drawer) {
            document.dispatchEvent(
              new CustomEvent('brick:cart:add', { detail: { item: data, sections: data.sections, source: this.button } })
            );
          } else {
            window.location.href = (theme.routes && theme.routes.cart) || `${root}cart`;
          }
          this.resetTimer = setTimeout(() => delete this.button.dataset.state, 2200);
        } catch (error) {
          this.button.removeAttribute('aria-busy');
          this.status.textContent = error.message || this.dataset.textError;
          this.showError(error.message || this.dataset.textError);
        }
      }

      showError(message) {
        let slot = this.querySelector('.accessories__error');
        if (!slot) {
          slot = document.createElement('p');
          slot.className = 'field__error accessories__error';
          slot.setAttribute('role', 'alert');
          this.button.after(slot);
        }
        slot.textContent = message;
        clearTimeout(this.errorTimer);
        this.errorTimer = setTimeout(() => slot.remove(), 5000);
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <brick-share>                                                          */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-share',
    class BrickShare extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-share]');
        this.status = this.querySelector('[data-share-status]');
        if (this.button) this.button.addEventListener('click', () => this.share());
      }

      async share() {
        const url = this.dataset.url || window.location.href;
        const coarse = window.matchMedia('(pointer: coarse)').matches;
        if (navigator.share && coarse) {
          try {
            await navigator.share({ title: this.dataset.title, url });
            return;
          } catch (error) {
            if (error.name === 'AbortError') return;
          }
        }
        try {
          await navigator.clipboard.writeText(url);
          this.say(this.dataset.textCopied);
        } catch (error) {
          window.prompt('', url);
        }
      }

      say(text) {
        if (!this.status) return;
        this.status.textContent = text;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => (this.status.textContent = ''), 2600);
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <brick-recommendations>                                                */
  /* ---------------------------------------------------------------------- */
  define(
    'brick-recommendations',
    class BrickRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded === 'true' || !this.dataset.url) return;
        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async load() {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fresh = doc.querySelector('brick-recommendations');
          if (!fresh || fresh.dataset.loaded !== 'true') return;
          this.innerHTML = fresh.innerHTML;
          this.dataset.loaded = 'true';
          this.hidden = false;
        } catch (error) {
          /* recommendations are optional */
        }
      }
    }
  );
})();
